import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Card,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { toast } from 'react-toastify';
import { paymentStatusMap } from '../../../components/constants';
import axiosInstance from '../../../services/axiosInstance';
import TotalPaymentDialog from './TotalPaymentDialog';
import { useNavigate } from 'react-router-dom';
import { CalendarMonth, AccessTime, SportsTennis } from '@mui/icons-material';
import { UserContext } from '../../../hooks';

const BookingDetailsCard = ({
  booking,
  setBooking,
  players,
  isBookingOwner,
}) => {
  const { isMember } = useContext(UserContext); // 🔥 Obtiene isMember del contexto
  const [isRainy, setIsRainy] = useState(false);
  const [totalPaymentDialogOpen, setTotalPaymentDialogOpen] = useState(false);
  const [confirmCancelDialogOpen, setConfirmCancelDialogOpen] = useState(false);
  const bookingDate = new Date(booking.dateBooked);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWeatherData = async () => {
      try {
        const response = await axiosInstance.post('/weather', {
          date: booking.dateBooked,
          days: 1,
        });

        // Verifica si en algún momento del día hay probabilidad de lluvia mayor al 10%
        const hasRain = response.weather.some(
          interval => interval.values.precipitationProbability >= 10
        );

        if (hasRain) {
          setIsRainy(true);
        }
      } catch (error) {
        console.error('Error al obtener el pronóstico del clima:', error);
      }
    };

    fetchWeatherData();
  }, [booking.dateBooked]);

  const handleTotalPaymentDialogOpen = () => setTotalPaymentDialogOpen(true);
  const handleTotalPaymentDialogClose = () => setTotalPaymentDialogOpen(false);
  const handleOpenConfirmCancelDialog = () => setConfirmCancelDialogOpen(true);
  const handleCloseConfirmCancelDialog = () =>
    setConfirmCancelDialogOpen(false);

  const getBookingStartTime = () => {
    const [startHour, startMinute] = booking.timeBooked
      .split(' - ')[0]
      .split(':'); // Split the time correctly
    if (!booking.dateBooked) {
      console.error('Selected date is undefined');
      return null;
    }

    return new Date(
      bookingDate.getFullYear(), // Extract year
      bookingDate.getMonth(), // Extract month
      bookingDate.getDate(), // Extract day
      parseInt(startHour), // Extract hour
      parseInt(startMinute) // Extract minute
    );
  };

  const isBeforeBookingTime = new Date() < getBookingStartTime();
  const hasAdditionalPlayers = Array.isArray(players) && players.length > 1;

  const getTotalPaymentTooltip = () => {
    if (isBeforeBookingTime) {
      return 'El pago solo se puede realizar después de la hora de la reserva';
    }

    if (!hasAdditionalPlayers) {
      return 'Debes añadir al menos un jugador para poder pagar el total.';
    }

    return '';
  };

  const totalPaymentTooltip = getTotalPaymentTooltip();

  const isCancelable = () => {
    if (!isBookingOwner) return false;

    const startTime = getBookingStartTime();
    if (!startTime) return false;
    const now = new Date();
    const diffMs = startTime - now;
    const diffHours = diffMs / (1000 * 3600);
    return (
      ((diffHours >= 6 || isRainy) &&
        booking.paymentStatus !== 'P' &&
        (isMember || booking.isLeagueMatch)) ||
      booking.totalAmount === 0
    );
  };

  const handlePaymentSuccess = () => {
    booking.paymentStatus = 'P';
    setBooking(booking);
  };

  const handleCancel = async () => {
    if (!isBookingOwner) {
      toast.error('Solo el socio que realizó la reserva puede cancelarla.');
      return;
    }

    try {
      const isPaidOrPartial =
        booking.paymentStatus === 'P' || booking.paymentStatus === 'S';
      const endpoint = isPaidOrPartial
        ? `/users/booking/${booking._id}/delete-booking-payments`
        : `/users/booking/${booking._id}/delete`;

      const response = await axiosInstance.delete(endpoint, { players });

      if (response.success) {
        navigate('/bookings');
        toast.success(
          isPaidOrPartial
            ? 'Reserva cancelada. El importe será depositado en tu monedero.'
            : 'Reserva cancelada exitosamente.'
        );
      } else {
        toast.error(response.message || 'No se pudo cancelar la reserva.');
      }
    } catch (error) {
      console.error('Error cancelando reserva:', error);
      toast.error('Error al intentar cancelar la reserva.');
    }
  };

  const handleConfirmCancel = () => {
    handleCancel();
    handleCloseConfirmCancelDialog();
  };

  return (
    <Card
      sx={{
        mb: 3,
        p: 3,
        border: '1px solid #f0f0f0',
        backgroundColor: '#fafafa',
      }}
    >
      <Typography variant="h5" color="primary" gutterBottom>
        Detalles de la Reserva
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Información general */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <CalendarMonth color="primary" />
            <Typography>
              <strong>Fecha:</strong>{' '}
              {new Date(booking.dateBooked)
                .toLocaleDateString('es-ES', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
                .replace(/^[a-zñ]/, c => c.toUpperCase())}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <AccessTime color="primary" />
            <Typography>
              <strong>Hora:</strong>{' '}
              {Array.isArray(booking.timeBooked)
                ? booking.timeBooked.join(', ')
                : booking.timeBooked}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <SportsTennis color="primary" />
            <Typography>
              <strong>{booking.courtId.type?.toUpperCase()} - </strong>{' '}
              {booking.courtId.name}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Chip
              label={
                paymentStatusMap[booking.paymentStatus]?.label || 'Desconocido'
              }
              color={
                paymentStatusMap[booking.paymentStatus]?.color || 'default'
              }
              size="medium"
            />
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Precio total y acciones */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6" color="primary">
            <strong>Total a pagar:</strong> {booking.totalAmount} €
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          }}
        >
          {isBookingOwner && (
            <Button
              variant="contained"
              color="error"
              onClick={handleOpenConfirmCancelDialog}
              disabled={!isCancelable()}
            >
              Cancelar Reserva
            </Button>
          )}
          {isBookingOwner && booking.paymentStatus !== 'P' && (
            <Tooltip title={totalPaymentTooltip}>
              <span>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleTotalPaymentDialogOpen}
                  disabled={Boolean(totalPaymentTooltip)}
                >
                  Pagar Total ({booking.totalDue} €)
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {isBookingOwner &&
        isRainy &&
        (isMember || booking.isLeagueMatch) &&
        booking.paymentStatus !== 'P' && (
          <p style={{ color: 'red', textAlign: 'right' }}>
            Se permite la cancelación sin restricciones debido a condiciones
            climáticas adversas.
          </p>
        )}

      <TotalPaymentDialog
        open={totalPaymentDialogOpen}
        onClose={handleTotalPaymentDialogClose}
        bookingId={booking._id}
        totalAmount={booking.totalDue}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Diálogo de confirmación para cancelar */}
      <Dialog
        open={confirmCancelDialogOpen}
        onClose={handleCloseConfirmCancelDialog}
      >
        <DialogTitle>¿Cancelar Reserva?</DialogTitle>
        <DialogContent>
          <Typography>
            {booking.paymentStatus === 'P' || booking.paymentStatus === 'S'
              ? 'Esta reserva ha sido pagada total o parcialmente. Si la cancelas, el importe correspondiente será reembolsado en tu monedero.'
              : '¿Estás seguro de que deseas cancelar esta reserva? Esta acción no se puede deshacer.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmCancelDialog} color="secondary">
            No, Volver
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
          >
            Sí, Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default BookingDetailsCard;
